import { FailedSendStatus } from '@/classes/SubmissionStatuses/FailedSendStatus'
import { ErrorStatus } from '@/classes/SubmissionStatuses/InsuranceStatuses/ErrorStatus'
import { PendingStatus } from '@/classes/SubmissionStatuses/InsuranceStatuses/PendingStatus'
import { SuccessStatus } from '@/classes/SubmissionStatuses/InsuranceStatuses/SuccessStatus'
import { WaitingForSignatureStatus } from '@/classes/SubmissionStatuses/InsuranceStatuses/WaitingForSignatureStatus'
import { ManuallyResolvedStatus } from '@/classes/SubmissionStatuses/ManuallyResolvedStatus'
import { NoneSentStatus } from '@/classes/SubmissionStatuses/NoneSentStatus'
import type StatusObject from '@/classes/SubmissionStatuses/StatusObject'
import {
	type LocalSubmissionStatus,
	SubmissionStatusMapper,
} from '@/classes/SubmissionStatuses/SubmissionStatusMapper'
import { UnknownStatus } from '@/classes/SubmissionStatuses/UnknownStatus'
import type {
	SubmissionAction,
	SubmissionStatus,
} from '@/classes/SubmissionStatuses/types'

enum InsuranceStatus {
	PENDING = 'Pending',
	WAITING_FOR_SIGNATURE = 'Waiting for Signature',
	SIGNATURE_SEND_FAILED = 'Signature Send-Off Failed',
	SIGNATURE_PROCESS_FAILED = 'Signature Process Failed',
	ACCEPTED = 'Accepted',
	REJECTED = 'Rejected',
	ERROR_AT_DESTINATION = 'Error at Destination',
	ERROR_PROCESSING_SUBMISSION = 'Error Processing Submission',
	MANUALLY_RESOLVED = 'Manually Resolved',
}

export class InsuranceStatusMapper extends SubmissionStatusMapper {
	submissionStatusObject: StatusObject
	constructor(
		submissionStatus?: SubmissionStatus,
		localSubmissionStatus: LocalSubmissionStatus[] = []
	) {
		super(localSubmissionStatus)
		this.submissionStatusObject = this.toStatusObject(submissionStatus)
	}

	toStatusObject(submissionStatus?: SubmissionStatus): StatusObject {
		const failedChannels = this.getFailedOutgoingChannels(['NGL'])
		if (failedChannels.length && !submissionStatus) {
			return new FailedSendStatus('NGL', failedChannels)
		}
		if (!submissionStatus) {
			return new NoneSentStatus('NGL')
		}

		switch (submissionStatus.status) {
			case InsuranceStatus.ACCEPTED:
				return new SuccessStatus(submissionStatus)
			case InsuranceStatus.MANUALLY_RESOLVED:
				return new ManuallyResolvedStatus(submissionStatus, 'NGL')
			case InsuranceStatus.REJECTED:
			case InsuranceStatus.ERROR_PROCESSING_SUBMISSION:
			case InsuranceStatus.ERROR_AT_DESTINATION:
			case InsuranceStatus.SIGNATURE_SEND_FAILED:
			case InsuranceStatus.SIGNATURE_PROCESS_FAILED:
				return new ErrorStatus(submissionStatus)

			case InsuranceStatus.PENDING:
				return new PendingStatus(submissionStatus)
			case InsuranceStatus.WAITING_FOR_SIGNATURE:
				return new WaitingForSignatureStatus(submissionStatus)

			default:
				return new UnknownStatus(submissionStatus)
		}
	}

	getVendorName(): string | undefined {
		return this.submissionStatusObject?.getVendorName()
	}

	getThemeVariant() {
		return this.submissionStatusObject.getThemeVariant()
	}

	getActions(): Array<SubmissionAction> {
		return this.submissionStatusObject.getActions()
	}

	getStatusText(): string {
		return this.submissionStatusObject.getStatusText()
	}

	getToolTipText() {
		return this.submissionStatusObject.getToolTipText()
	}

	getUpdatedAt(): string {
		return this.submissionStatusObject.getUpdatedAt()
	}
}
